import {
  Button,
  Divider,
  EButtonColorVariant,
  EHeadingStyleVariant,
  EModalVariants,
  GeneralCaretDownIcon,
  Heading,
  Modal,
  Overlay,
  Radio,
  RadioGroup,
  Text,
} from '@outdoorsyco/bonfire';
import { clsx } from 'clsx';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { useMenuTriggerState } from 'react-stately';

import { EPage, PAGE_PATHNAME } from '@/constants/pages';
import { useOptimizelySortOptions } from '@/constants/search';
import { ESearchFilters, SERP_URLS } from '@/constants/searchFilters';
import { useBreakpoint } from '@/hooks/useBreakpoint';
import { useIsFirstRender } from '@/hooks/useIsFirstRender';
import { useLocaleSettings } from '@/hooks/useLocaleSettings';
import { useLocalStorage } from '@/hooks/useLocalStorage';
import { useRouter } from '@/hooks/useRouter';
import { applySearchFilter } from '@/redux/modules/search';
import { EFilterMethod } from '@/redux/modules/searchForm';
import { getQueryParams } from '@/redux/selectors/queryParams';
import { getSearchResultsMeta } from '@/redux/selectors/search/meta';
import { ESearchSort } from '@/services/analytics/listings/types';
import { ESearchEventName } from '@/services/analytics/search/types';
import { EProjectSection, trackEventV2 } from '@/services/analytics/trackEventV2';
import { ERentalType } from '@/services/analytics/types';
import { OptimizelyFlags, useOptimizelyDecision } from '@/services/experiments';
import {
  getSortLogicFromOptimizely,
  getSortLogicNamesFromOptimizely,
} from '@/services/experiments/sortOrder';
import { ERentalCategory } from '@/services/types/search/rentals/id';
import { getParamAsString } from '@/utility/queryParams';

const RESET_PAGE_PARAMS: Partial<Record<ESearchFilters, string>> = {
  [ESearchFilters.PAGE_OFFSET]: '0',
};

const ResultsSort = () => {
  const intl = useIntl();
  const router = useRouter();
  const dispatch = useDispatch();
  const { isMobile } = useBreakpoint();
  const isFirstRender = useIsFirstRender();
  const { total } = useSelector(getSearchResultsMeta);
  const filtersFromQuery = useSelector(getQueryParams);
  const [prevBaseUrl] = useLocalStorage('prevBaseUrl');
  const isEscapesSearchPage = router?.pathname?.includes(PAGE_PATHNAME[EPage.ESCAPES_SEARCH]);
  const isCampgroundsSearchPage = router?.pathname?.includes(
    PAGE_PATHNAME[EPage.CAMPGROUND_SEARCH],
  );

  const initialSelectedOption = getParamAsString(router.query.sort);
  const rentalCategoryFromQuery =
    ((filtersFromQuery && filtersFromQuery[ESearchFilters.RENTAL_CATEGORY]) as ERentalCategory) ||
    ERentalCategory.RV;

  const [isOpenMenu, setIsOpenMenu] = useState(false);
  const [selectedOption, setSelectedOption] = useState(initialSelectedOption ?? '');

  const searchSortLogicDecision = useOptimizelyDecision(OptimizelyFlags.SEARCH_SORT_LOGIC);
  const sortOrder = getSortLogicFromOptimizely(searchSortLogicDecision);
  const sortOrderNames = getSortLogicNamesFromOptimizely(searchSortLogicDecision);

  const options = useOptimizelySortOptions();
  const { locale } = useLocaleSettings();

  const dropdownRef = useRef(null);
  const dropdownTriggerState = useMenuTriggerState({
    isOpen: isOpenMenu,
    onOpenChange: open => {
      setIsOpenMenu(open);
    },
  });

  const sortOptions = useMemo(() => {
    const resultOptions = sortOrder.filter(sort => !!options[sort]).map(sort => options[sort]);
    if (sortOrderNames && locale === 'en-us') {
      return resultOptions.map((op, i) => {
        const label = sortOrderNames[i];

        if (op && label) {
          return { ...op, label };
        }

        return op;
      });
    }

    return resultOptions;
  }, [locale, options, sortOrder, sortOrderNames]);

  const trackSortUpdate = (newSort: string) => {
    if (isFirstRender) return;

    const previousListingsSort = initialSelectedOption || 'recommended';
    const listingsSort = newSort || 'recommended';

    if (previousListingsSort === listingsSort) return;

    const getRentalType = (category: ERentalCategory): ERentalType => {
      switch (category) {
        case ERentalCategory.STAY:
          return ERentalType.STAY;
        case ERentalCategory.CAMPGROUND:
          return ERentalType.CAMPGROUND;
        default:
          return ERentalType.RV;
      }
    };

    const eventProps = {
      rentalType: getRentalType(rentalCategoryFromQuery),
      listingsSort: mapSortToAnalytics(listingsSort),
      previousListingsSort: mapSortToAnalytics(previousListingsSort),
      previousFilters: filtersFromQuery,
      newFilters: { ...filtersFromQuery, sort: listingsSort },
      filterType: [ESearchFilters.SORT],
      filterMethod: EFilterMethod.DROPDOWN,
      isSEO: prevBaseUrl ? SERP_URLS.includes(prevBaseUrl) : false,
    };

    trackEventV2(ESearchEventName.SEARCH_SORT_UPDATED, EProjectSection.MARKETPLACE, eventProps);
  };

  const mapSortToAnalytics = (sort: string): ESearchSort => {
    switch (sort) {
      case 'price':
        return ESearchSort.PRICE_LOW;
      case '-price':
        return ESearchSort.PRICE_HIGH;
      case 'distance':
        return ESearchSort.CLOSEST_FIRST;
      case '-num_reviews,-rating':
        return ESearchSort.TOP_RATED;
      default:
        return ESearchSort.RECOMMENDED;
    }
  };

  useEffect(() => {
    // Leaving out isFirstRender and isMobile intentionally
    if (!isFirstRender && !isMobile) {
      trackSortUpdate(selectedOption);
      dispatch(
        applySearchFilter(
          { ...RESET_PAGE_PARAMS, sort: selectedOption },
          false,
          false,
          false,
          undefined,
          false,
        ),
      );
    }
  }, [selectedOption]); // eslint-disable-line react-hooks/exhaustive-deps

  const onCloseModalHandler = () => {
    setSelectedOption(initialSelectedOption || '');
    setIsOpenMenu(false);
  };

  const onConfirmHandler = () => {
    trackSortUpdate(selectedOption);
    dispatch(
      applySearchFilter(
        { ...RESET_PAGE_PARAMS, sort: selectedOption },
        false,
        false,
        false,
        undefined,
        false,
      ),
    );
    setIsOpenMenu(false);
  };

  const iconClassName = clsx('ml-1 transition-all duration-150', {
    '!rotate-180': isOpenMenu,
  });

  const totalResultsLabel = isEscapesSearchPage ? (
    <FormattedMessage
      id="VYTkwd"
      defaultMessage="{total, plural, =1 {# Escape} other {# Escapes}}"
      values={{ total }}
    />
  ) : isCampgroundsSearchPage ? (
    <FormattedMessage
      id="Lvl2L0"
      defaultMessage="{total, plural, =1 {# Campground} other {# Campgrounds}}"
      values={{ total }}
    />
  ) : (
    <FormattedMessage
      id="YGbIDb"
      defaultMessage="{total, plural, =1 {# RV rental} other {# RV rentals}}"
      values={{ total }}
    />
  );

  return (
    <div className="flex justify-between">
      <Text className="text-sm font-medium md:text-base">
        {total >= 1000 ? (
          <FormattedMessage id="NxO9HJ" defaultMessage="Over 1,000 Rentals" />
        ) : (
          totalResultsLabel
        )}
      </Text>

      {/* hide sorting options for escapes search page for now */}
      {!isEscapesSearchPage && (
        <>
          <div className="flex items-center gap-2">
            <Text className={clsx('text-sm', { 'text-gray-50': isMobile })}>
              <FormattedMessage defaultMessage="Sort by" id="hDI+JM" />
            </Text>
            <button
              ref={isOpenMenu ? dropdownRef : null}
              onClick={() => setIsOpenMenu(!isOpenMenu)}>
              <Text
                className={clsx('flex items-center justify-center font-medium text-gray-900', {
                  ['text-sm']: isMobile,
                })}>
                <span className="flex items-center justify-center">
                  {sortOptions.find(o => o.value === selectedOption || '')?.shortLabel}
                </span>
                <GeneralCaretDownIcon className={iconClassName} />
              </Text>
            </button>
          </div>
          {!isMobile && isOpenMenu && (
            <Overlay
              triggerRef={dropdownRef}
              triggerState={dropdownTriggerState}
              popperProps={{
                placement: 'bottom-end',
              }}
              className="z-[1310]"
              underlay
              underlayClassname="z-[1299]">
              <div className="flex flex-col items-start p-4 mt-2 bg-white shadow-100 rounded-box gap-3">
                {sortOptions.map(option => {
                  return (
                    <button
                      key={option.value}
                      onClick={() => {
                        setSelectedOption(option.value);
                        setIsOpenMenu(false);
                      }}>
                      <Text>{option.label}</Text>
                    </button>
                  );
                })}
              </div>
            </Overlay>
          )}
          {isMobile && isOpenMenu && (
            <Modal
              isOpen={isOpenMenu}
              onClose={onCloseModalHandler}
              variant={EModalVariants.Bottom}>
              <Modal.Actions />
              <Modal.Header inlineHeader={true}>
                <Heading variant={EHeadingStyleVariant.H6}>
                  <FormattedMessage
                    defaultMessage="Sort {total} vehicles"
                    id="R0IjWM"
                    values={{ total }}
                  />
                </Heading>
              </Modal.Header>
              <Modal.Divider />
              <Modal.Content className="my-4">
                <RadioGroup name="sort" inputValue={selectedOption} onChange={setSelectedOption}>
                  {sortOptions.map((option, index) => {
                    return (
                      <div key={option.value} className="flex flex-col gap-4">
                        <button
                          onClick={() => setSelectedOption(option.value)}
                          className="flex items-center justify-between w-full">
                          <Text>{option.label}</Text>
                          <Radio ariaLabel={option.label} value={option.value} />
                        </button>
                        {index !== sortOptions.length - 1 && <Divider className="mb-2" />}
                      </div>
                    );
                  })}
                </RadioGroup>
              </Modal.Content>
              <Modal.Divider />
              <Modal.Footer className="p-4">
                <Button
                  label={intl.formatMessage({ defaultMessage: 'Confirm', id: 'N2IrpM' })}
                  variant={EButtonColorVariant.Primary}
                  onClick={onConfirmHandler}
                  className="w-full"
                />
              </Modal.Footer>
            </Modal>
          )}
        </>
      )}
    </div>
  );
};

export default ResultsSort;
